import { useParams, useNavigate } from "react-router-dom";
import MiddleBlock from "../../components/MiddleBlock/MiddleBlock";
import { getEventByID, updateEventByID, createEvent, updateGallery, loadGalleryByID, getApiErrorMessage } from "../../api/api";
import { useCallback, useEffect, useState, useRef } from "react";
import EditWydarzenia from "./components/EditWydarzenia/EditWydarzenia";
import "./EditEvent.css"
import FullEventBlock from "../ViewEvent/components/FullEventBlock/FullEventBlock";
import { Chrome } from '@uiw/react-color';
import { formatDate } from "../../utils/formatDate";
import Loader from "../../components/elements/Loader/Loader";
import { DEFAULT_IMAGE_POSITION, parseImagePosition, serializeImagePosition } from "../../utils/imagePosition";

// ВЫНЕСИ ЗА ПРЕДЕЛЫ КОМПОНЕНТА (перед export default)
const convertToWebP = (file) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
            const img = new Image();
            img.src = e.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, img.width, img.height);
                
                canvas.toBlob((blob) => {
                    if (!blob) {
                        resolve(file);
                        return;
                    }
                    const webpFile = new File(
                        [blob], 
                        file.name.replace(/\.[^/.]+$/, '') + '.webp', 
                        { type: 'image/webp' }
                    );
                    resolve(webpFile);
                }, 'image/webp', 0.92);
            };
            img.onerror = () => resolve(file);
        };
        reader.onerror = (error) => {
            console.error('Ошибка чтения файла:', error);
            resolve(file);
        };
    });
};

export default function EditEvent({ type }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(type === 'create' ? false : true);
    const [hex, setHex] = useState({
        background: "#290401",
        primary: "#EAD9CB",
        secondary: "#F9C49A"
    })
    
    const [imageStyle, setImageStyle] = useState({
        top: 0,
        left: 0,
        scale: 1
    });
    const [mobileImageStyle, setMobileImageStyle] = useState({ ...DEFAULT_IMAGE_POSITION });
    const [previewMode, setPreviewMode] = useState('desktop');
    
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [initialPos, setInitialPos] = useState({ top: 0, left: 0 });
    const [previewImage, setPreviewImage] = useState(null);
    const [imageKey, setImageKey] = useState(Date.now());
    
    const [galleryImages, setGalleryImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileGalleryInputRef = useRef(null);
    
    const imageRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (type === 'create') {
            setEvent({
                id: null,
                title: '',
                short_description: '',
                full_description: '',
                event_date: '',
                image: '',
                image_position: '',
                image_position_mobile: '',
                links: {}
            });
            setImageStyle({ ...DEFAULT_IMAGE_POSITION });
            setMobileImageStyle({ ...DEFAULT_IMAGE_POSITION });
            setHex({
                background: "#290401",
                primary: "#EAD9CB",
                secondary: "#F9C49A"
            });
            setLoading(false);
            return;
        }

        async function loadEvent() {
            try {
                const data = await getEventByID(id);
                setEvent(data);
                setImageStyle(parseImagePosition(data.image_position));
                setMobileImageStyle(parseImagePosition(data.image_position_mobile || data.image_position));
                
                if (data.colors) {
                    try {
                        const parsedColors = JSON.parse(data.colors);
                        setHex(parsedColors);
                    } catch (e) {
                        console.log('Ошибка парсинга colors:', e);
                    }
                }
                
                if (data.links) {
                    try {
                        const parsedLinks = JSON.parse(data.links);
                        console.log('Распарсенные ссылки:', parsedLinks);
                        
                        const instagram = parsedLinks.find(l => l.name === 'instagram')?.link || '';
                        const facebook = parsedLinks.find(l => l.name === 'facebook')?.link || '';
                        const phone_nubmer = parsedLinks.find(l => l.name === 'phone')?.link || '';
                        
                        setEvent(prev => ({
                            ...prev,
                            instagram,
                            facebook,
                            phone_nubmer,
                            links: parsedLinks
                        }));
                    } catch (e) {
                        console.log('Ошибка парсинга links:', e);
                    }
                }
                
                console.log('Событие загружено:', data);
            } catch (error) {
                console.log('Ошибка:', error);
            } finally {
                setLoading(false);
            }
        }
        loadEvent();
    }, [id, type]);

    // ===== ЗАГРУЗКА ГАЛЕРЕИ =====
    useEffect(() => {
        if (type === 'create' || !id) {
            setGalleryImages([]);
            return;
        }

        async function loadGallery() {
            try {
                // Загружаем галерею с сервера
                const response = await loadGalleryByID(id);
                
                if (response.success && response.data) {
                    // Преобразуем данные из БД в формат для компонента
                    const images = response.data.map(img => ({
                        id: img.id,
                        path: img.image_path, // Путь к изображению на сервере
                        caption: img.caption || '',
                        sort_order: img.sort_order || 0,
                        isNew: false // Это уже существующие изображения
                    }));
                    
                    setGalleryImages(images);
                    console.log(`📸 Загружено ${images.length} изображений для события ${id}`);
                } else {
                    // Если нет изображений, ставим пустой массив
                    setGalleryImages([]);
                    console.log('📸 Нет изображений для этого события');
                }
            } catch (error) {
                console.error('❌ Ошибка загрузки галереи:', error);
                // В случае ошибки ставим пустой массив, чтобы не показывать заглушку
                setGalleryImages([]);
            }
        }
        
        loadGallery();
    }, [id, type]);

    const activeImageStyle = previewMode === 'mobile' ? mobileImageStyle : imageStyle;
    const updateActiveImageStyle = useCallback((updater) => {
        const setter = previewMode === 'mobile' ? setMobileImageStyle : setImageStyle;
        setter(updater);
    }, [previewMode]);

    useEffect(() => {
        const handleWheel = (e) => {
            if (imageRef.current && imageRef.current.contains(e.target)) {
                e.preventDefault();
                e.stopPropagation();
                
                const delta = e.deltaY > 0 ? -0.05 : 0.05;
                updateActiveImageStyle(prev => ({
                    ...prev,
                    scale: Math.max(0.5, Math.min(3, prev.scale + delta))
                }));
            }
        };

        document.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            document.removeEventListener('wheel', handleWheel);
        };
    }, [updateActiveImageStyle]);

    const handleMouseDown = (e) => {
        e.preventDefault();
        setIsDragging(true);
        setDragStart({ x: e.clientX, y: e.clientY });
        setInitialPos({
            top: activeImageStyle.top,
            left: activeImageStyle.left
        });
    };

    const handleMouseMove = useCallback((e) => {
        if (!isDragging) return;
        
        const dx = e.clientX - dragStart.x;
        const dy = e.clientY - dragStart.y;
        
        updateActiveImageStyle(prev => ({
            ...prev,
            top: initialPos.top + dy,
            left: initialPos.left + dx
        }));
    }, [dragStart, initialPos, isDragging, updateActiveImageStyle]);

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target?.tagName) || e.target?.isContentEditable) {
                return;
            }
            const step = e.shiftKey ? 10 : 1;
            
            switch(e.key) {
                case 'ArrowUp':
                    e.preventDefault();
                    updateActiveImageStyle(prev => ({ ...prev, top: prev.top - step }));
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    updateActiveImageStyle(prev => ({ ...prev, top: prev.top + step }));
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    updateActiveImageStyle(prev => ({ ...prev, left: prev.left - step }));
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    updateActiveImageStyle(prev => ({ ...prev, left: prev.left + step }));
                    break;
                case 'r':
                case 'R':
                    updateActiveImageStyle({ ...DEFAULT_IMAGE_POSITION });
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, updateActiveImageStyle]);

    // Добавь новое состояние для файла
    const [selectedImageFile, setSelectedImageFile] = useState(null);

    const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
        console.log('📁 Выбран файл:', file.name);
        console.log('📁 Размер:', (file.size / 1024 / 1024).toFixed(2), 'MB');
        console.log('📁 Тип:', file.type);
        
        let processedFile = file;
        
        // Конвертируем в WebP если файл большой или не WebP
        if (file.size > 500 * 1024 || (file.type !== 'image/webp')) {
            console.log('🔄 Конвертация в WebP...');
            try {
                processedFile = await convertToWebP(file);
                console.log('✅ Конвертировано в WebP:', (file.size / 1024 / 1024).toFixed(2), 'MB ->', (processedFile.size / 1024 / 1024).toFixed(2), 'MB');
                console.log('📁 Новый тип:', processedFile.type);
            } catch (error) {
                console.error('❌ Ошибка конвертации:', error);
                processedFile = file;
            }
        }
        
        // Сохраняем файл
        setSelectedImageFile(processedFile);
        
        // Создаем превью
        const objectUrl = URL.createObjectURL(processedFile);
        setPreviewImage(objectUrl);
        
        // Сохраняем имя для отображения
        setEvent(prev => ({
            ...prev,
            image: processedFile.name
        }));
        
        setImageKey(Date.now());
    }
};

    const handleImageClick = () => {
        fileInputRef.current?.click();
    };

    const handleGalleryFiles = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        const invalidFile = files.find(file => !allowedTypes.includes(file.type));
        if (invalidFile) {
            alert(`Nieobsługiwany format pliku: ${invalidFile.name}`);
            return;
        }
        
        setUploading(true);
        
        try {
            const newImages = await Promise.all(
                files.map(async (file, index) => {
                    // Конвертируем в WebP если файл большой или не WebP
                    let processedFile = file;
                    
                    if (file.size > 500 * 1024 || file.type !== 'image/webp') {
                        try {
                            processedFile = await convertToWebP(file);
                            console.log(`✅ Галерея: ${file.name} -> WebP: ${(file.size/1024/1024).toFixed(2)}MB -> ${(processedFile.size/1024/1024).toFixed(2)}MB`);
                        } catch (error) {
                            console.error(`❌ Ошибка конвертации ${file.name}:`, error);
                            processedFile = file;
                        }
                    }
                    
                    // Создаем URL для превью
                    const previewUrl = URL.createObjectURL(processedFile);
                    
                    return {
                        id: Date.now() + index,
                        path: previewUrl,
                        caption: processedFile.name.replace(/\.[^/.]+$/, ""),
                        file: processedFile,
                        isNew: true
                    };
                })
            );
            
            console.log('📁 Добавлено файлов в галерею:', newImages.length);
            setGalleryImages(prev => [...prev, ...newImages]);
            
        } catch (error) {
            console.error('Ошибка загрузки:', error);
            alert('Nie udało się wczytać obrazów!');
        } finally {
            setUploading(false);
            if (fileGalleryInputRef.current) {
                fileGalleryInputRef.current.value = '';
            }
        }
    };

    const handleDeleteImage = async (imageId) => {
        
        try {
            const image = galleryImages.find(img => img.id === imageId);
            if (image?.path?.startsWith('blob:')) {
                URL.revokeObjectURL(image.path);
            }
            
            setGalleryImages(prev => prev.filter(img => img.id !== imageId));
            
        } catch (error) {
            console.error('Ошибка удаления:', error);
            alert('Nie udało się usunąć obrazu!');
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            const fileList = { target: { files: files } };
            handleGalleryFiles(fileList);
        }
    };

    const handleGalleryClick = () => {
        fileGalleryInputRef.current?.click();
    };

    const saveGallery = async (eventId) => {
        try {
            const formData = new FormData();
            
            formData.append('event_id', eventId);
            
            // Подготавливаем данные галереи
            const galleryData = galleryImages.map((img, index) => ({
                id: img.id,
                caption: img.caption || '',
                sort_order: index,
                isNew: img.isNew || false
            }));
            formData.append('gallery', JSON.stringify(galleryData));
            
            // ===== ВАЖНО: правильная отправка файлов =====
            const newFiles = galleryImages.filter(img => img.isNew && img.file);
            console.log('📤 Новых файлов для отправки:', newFiles.length);
            
            // Проверяем каждый файл
            newFiles.forEach((img, index) => {
                console.log(`📤 Файл ${index}:`, img.file.name, img.file.size, img.file.type);
                formData.append('new_images[]', img.file);
            });
            
            // Логируем все что отправляем
            console.log('📤 FormData содержимое:');
            for (let pair of formData.entries()) {
                if (pair[1] instanceof File) {
                    console.log(`  ${pair[0]}: ${pair[1].name} (${pair[1].size} bytes)`);
                } else {
                    console.log(`  ${pair[0]}: ${pair[1]}`);
                }
            }
            
            // Отправляем
            const response = await updateGallery(formData);
            console.log('📥 Ответ сервера:', response);
            
            if (response.success) {
                // Обновляем состояние - убираем флаг isNew
                setGalleryImages(prev => prev.map(img => ({
                    ...img,
                    isNew: false
                })));
                
            }
            
        } catch (error) {
            console.error('❌ Ошибка сохранения галереи:', error);
            throw error;
        }
    };

    // Добавь эту функцию в компонент

    const validateBeforeSave = () => {
        if (!event?.title?.trim()) {
            throw new Error('Tytuł wydarzenia jest wymagany.');
        }
        if (!event?.event_date?.trim()) {
            throw new Error('Data wydarzenia jest wymagana.');
        }
    };

    const handleApply = async () => {
    try {
        validateBeforeSave();
        const formData = new FormData();

        const links = [
            { name: "instagram", link: event?.instagram || "" },
            { name: "facebook", link: event?.facebook || "" },
            { name: "phone", link: event?.phone_nubmer || "" }
        ].filter(link => link.link.trim() !== "");
        
        formData.append('id', event?.id);
        formData.append('title', event?.title || '');
        formData.append('short_description', event?.short_description || event?.description || '');
        formData.append('full_description', event?.full_description || '');
        formData.append('font_size_full_description', event?.font_size_full_description || '20');
        formData.append('event_date', event?.event_date || '');
        formData.append('image_position', serializeImagePosition(imageStyle));
        formData.append('image_position_mobile', serializeImagePosition(mobileImageStyle));
        formData.append('colors', JSON.stringify(hex));
        formData.append('links', JSON.stringify(links));
        
        // ===== ВАЖНО: добавляем файл из состояния =====
        if (selectedImageFile) {
            console.log('📁 Добавляем файл для image:', selectedImageFile.name);
            formData.append('image', selectedImageFile);
        } else {
            console.log('📁 Нет нового файла для image, сохраняем существующий');
            // Если нет нового файла, но есть старый - передаем его имя
            if (event?.image) {
                formData.append('existing_image', event.image);
            }
        }
        
        // Te operacje są rozdzielone, dlatego osobno sprawdzamy ich odpowiedzi.
        await updateEventByID(formData);

        try {
            await saveGallery(event?.id);
        } catch (galleryError) {
            alert(`Dane wydarzenia zapisano, ale galeria nie została zapisana: ${getApiErrorMessage(galleryError)}`);
            return;
        }
        
        alert('Dane zostały zapisane!');
        navigate('/');
    } catch (error) {
        console.error('Ошибка:', error);
        alert(getApiErrorMessage(error, 'Wystąpił błąd podczas zapisu!'));
    }
};

// ===== ОБНОВЛЕННЫЙ handleCreate =====
const handleCreate = async () => {
    try {
        validateBeforeSave();
        const formData = new FormData();

        const links = [
            { name: "instagram", link: event?.instagram || "" },
            { name: "facebook", link: event?.facebook || "" },
            { name: "phone", link: event?.phone_nubmer || "" }
        ].filter(link => link.link.trim() !== "");
        
        formData.append('title', event?.title || '');
        formData.append('short_description', event?.short_description || event?.description || '');
        formData.append('full_description', event?.full_description || '');
        formData.append('font_size_full_description', event?.font_size_full_description || '20');
        formData.append('event_date', event?.event_date || '');
        formData.append('image_position', serializeImagePosition(imageStyle));
        formData.append('image_position_mobile', serializeImagePosition(mobileImageStyle));
        formData.append('colors', JSON.stringify(hex));
        formData.append('links', JSON.stringify(links));
        
        // ===== ВАЖНО: добавляем файл из состояния =====
        if (selectedImageFile) {
            console.log('📁 Добавляем файл для image:', selectedImageFile.name);
            formData.append('image', selectedImageFile);
        }
        
        // Создаем событие
        const response = await createEvent(formData);
        
        // Сохраняем галерею для нового события
        if (response && response.id) {
            try {
                await saveGallery(response.id);
            } catch (galleryError) {
                alert(`Wydarzenie utworzono, ale galeria nie została zapisana: ${getApiErrorMessage(galleryError)}`);
                navigate(`/edit-event/${response.id}`);
                return;
            }
        }
        
        alert('Wydarzenie zostało utworzone!');
        navigate('/');
    } catch (error) {
        console.error('Ошибка:', error);
        alert(getApiErrorMessage(error, 'Wystąpił błąd podczas tworzenia!'));
    }
};

    const handleCancel = () => {
        if (previewImage) {
            URL.revokeObjectURL(previewImage);
        }
        navigate('/');
    };

    if (loading) {
        return <Loader />
    }

    if (event?.error && type !== 'create') {
        return (
            <div style={{ height: "100%", width: "100%", display: "grid", placeItems: "center" }}>
                { event.error }
            </div>
        )
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        console.log(name, value)
        setEvent(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const buttonText = type === 'create' ? 'Utwórz' : 'Zastosuj';
    const handleSubmit = type === 'create' ? handleCreate : handleApply;
    const handlePositionFieldChange = (field, value) => {
        const numericValue = Number.parseFloat(value);
        if (!Number.isFinite(numericValue)) return;

        updateActiveImageStyle((current) => ({
            ...current,
            [field]: field === 'scale' ? Math.max(0.5, Math.min(3, numericValue)) : numericValue,
        }));
    };

    return (
        <>
            <MiddleBlock>
                <div className="edit-event-container">
                    <h1>Podgląd wydarzenia</h1>
                    <div className="image-position-editor">
                        <div className="image-position-tabs" role="tablist" aria-label="Wersja podglądu">
                            <button
                                type="button"
                                className={previewMode === 'desktop' ? 'active' : ''}
                                onClick={() => setPreviewMode('desktop')}
                            >
                                Komputer
                            </button>
                            <button
                                type="button"
                                className={previewMode === 'mobile' ? 'active' : ''}
                                onClick={() => setPreviewMode('mobile')}
                            >
                                Telefon
                            </button>
                        </div>

                        <div className="image-position-heading">
                            <div>
                                <strong>{previewMode === 'mobile' ? 'Pozycja obrazu na telefonie' : 'Pozycja obrazu na komputerze'}</strong>
                                <span>Przeciągaj obraz, używaj strzałek i rolki myszy albo wpisz wartości.</span>
                            </div>
                            <button
                                type="button"
                                className="image-position-reset"
                                onClick={() => updateActiveImageStyle({ ...DEFAULT_IMAGE_POSITION })}
                            >
                                Resetuj
                            </button>
                        </div>

                        <div className="image-position-fields">
                            <label>
                                Góra (px)
                                <input
                                    type="number"
                                    value={activeImageStyle.top}
                                    onChange={(e) => handlePositionFieldChange('top', e.target.value)}
                                />
                            </label>
                            <label>
                                Lewo (px)
                                <input
                                    type="number"
                                    value={activeImageStyle.left}
                                    onChange={(e) => handlePositionFieldChange('left', e.target.value)}
                                />
                            </label>
                            <label>
                                Skala
                                <input
                                    type="number"
                                    min="0.5"
                                    max="3"
                                    step="0.05"
                                    value={activeImageStyle.scale}
                                    onChange={(e) => handlePositionFieldChange('scale', e.target.value)}
                                />
                            </label>
                        </div>
                    </div>
                    <div 
                        ref={imageRef}
                        onMouseDown={handleMouseDown}
                        className={`event-preview-stage ${previewMode === 'mobile' ? 'is-mobile' : ''}`}
                    >
                        <EditWydarzenia
                            key={imageKey}
                            title={event?.title}
                            description={event?.short_description}
                            date={formatDate(event?.event_date)}
                            image={event?.image}
                            previewImage={previewImage}
                            imagePosition={event?.image_position}
                            imagePositionMobile={event?.image_position_mobile}
                            imageStyle={imageStyle}
                            imageStyleMobile={mobileImageStyle}
                            previewMode={previewMode}
                            hex={ hex }
                        />
                    </div>
                    <div className="edit-event-title-with-event">
                        <h1>Widok szczegółowy</h1>
                        <div 
                            className={`event-preview-stage ${previewMode === 'mobile' ? 'is-mobile' : ''}`}
                        >
                            <FullEventBlock
                                key={imageKey}
                                title={event?.title}
                                description={event?.full_description}
                                date={formatDate(event?.event_date)}
                                image={previewImage || event?.image}
                                imagePosition={event?.image_position}
                                imagePositionMobile={event?.image_position_mobile}
                                imageStyle={imageStyle}
                                imageStyleMobile={mobileImageStyle}
                                previewMode={previewMode}
                                hex={ hex }
                                actionType="edit"
                                fontSizeDescription={ event?.font_size_full_description || 20 }
                                links={[
                                    { name: "instagram", link: event?.instagram },
                                    { name: "facebook", link: event?.facebook },
                                    { name: "phone", link: event?.phone_nubmer },
                                ]}
                            />
                        </div>
                    </div>

                    <div className="edit-event-inputs">
                        <div className="edit-events-colors">
                            <div className="color-input-group">
                                <label className="input-label">Kolor tła</label>
                                <Chrome
                                    style={{ marginLeft: 20 }}
                                    color={hex.background}
                                    onChange={(color) => {
                                        setHex({
                                            ...hex,
                                            background: color.hex,
                                        });
                                    }}
                                />
                                <input 
                                    name="background_color"
                                    type="hidden"
                                    value={hex.background || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="color-input-group">
                                <label className="input-label">Kolor główny</label>
                                <Chrome
                                    style={{ marginLeft: 20 }}
                                    color={hex.primary}
                                    onChange={(color) => {
                                        setHex({
                                            ...hex,
                                            primary: color.hex,
                                        });
                                    }}
                                />
                                <input 
                                    name="primary_color"
                                    type="hidden"
                                    value={hex.primary || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="color-input-group">
                                <label className="input-label">Kolor dodatkowy</label>
                                <Chrome
                                    style={{ marginLeft: 20 }}
                                    color={hex.secondary}
                                    onChange={(color) => {
                                        setHex({
                                            ...hex,
                                            secondary: color.hex,
                                        });
                                    }}
                                />
                                <input 
                                    name="secondary_color"
                                    type="hidden"
                                    value={hex.secondary || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                        <div className="input-group">
                            <label className="input-label">Nazwa</label>
                            <input 
                                name="title"
                                type="text" 
                                className="input-field input-title"
                                value={event?.title || ''}
                                onChange={handleInputChange}
                                placeholder="Wpisz nazwę..."
                            />
                        </div>
                        
                        <div className="input-group">
                            <label className="input-label">Krótki opis</label>
                            <textarea
                                name="short_description"
                                className="input-field input-textarea"
                                value={event?.short_description || ''}
                                onChange={handleInputChange}
                                placeholder="Wpisz krótki opis..."
                                rows={4}
                            />
                        </div>
                        
                        <div className="input-group">
                            <label className="input-label">Długi opis</label>
                            <textarea
                                name="full_description"
                                className="input-field input-textarea"
                                value={event?.full_description || ''}
                                onChange={handleInputChange}
                                placeholder="Wpisz opis..."
                                rows={4}
                            />
                        </div>
                        <div className="input-group">
                            <label className="input-label">Rozmiar czcionki długiego opisu</label>
                            <input 
                                name="font_size_full_description"
                                type="text" 
                                className="input-field"
                                value={event?.font_size_full_description || ''}
                                onChange={handleInputChange}
                            />
                        </div>
                        
                        <div className="input-group">
                            <label className="input-label">Data</label>
                            <input 
                                name="event_date"
                                type="date" 
                                className="input-field input-date"
                                value={event?.event_date || ''}
                                onChange={handleInputChange}
                            />
                        </div>
                        
                        <div className="input-links-group">
                            <div className="input-link-group">
                                <label className="input-label">Instagram</label>
                                <input 
                                    name="instagram"
                                    type="text"
                                    placeholder="Zostaw puste"
                                    className="input-field"
                                    value={event?.instagram || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="input-link-group">
                                <label className="input-label">Facebook</label>
                                <input 
                                    name="facebook"
                                    type="text"
                                    placeholder="Zostaw puste"
                                    className="input-field"
                                    value={event?.facebook || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="input-link-group">
                                <label className="input-label">Numer telefonu</label>
                                <input 
                                    name="phone_nubmer"
                                    type="text"
                                    placeholder="Zostaw puste"
                                    className="input-field"
                                    value={event?.phone_nubmer || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                        
                        <div className="input-group">
                            <label className="input-label">Obraz</label>
                            <div className="image-upload-wrapper">
                                <input 
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                />
                                <button 
                                    type="button"
                                    className="btn-upload"
                                    onClick={handleImageClick}
                                >
                                    📁 Wybierz obraz
                                </button>
                                {event?.image && (
                                    <span className="image-name">
                                        {event.image}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="gallery-section">
                            <div className="gallery-header">
                                <h3 className="gallery-title">Galeria</h3>
                                <span className="gallery-count">
                                    Obrazów: {galleryImages.length}
                                </span>
                            </div>

                            <div 
                                className={`gallery-upload-zone ${dragOver ? 'drag-over' : ''}`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={handleGalleryClick}
                            >
                                <input
                                    ref={fileGalleryInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handleGalleryFiles}
                                    style={{ display: 'none' }}
                                />
                                
                                {uploading ? (
                                    <div className="gallery-upload-loading">
                                        <div className="spinner"></div>
                                        <p>Wczytywanie...</p>
                                    </div>
                                ) : (
                                    <div className="gallery-upload-content">
                                        <span className="upload-icon">📤</span>
                                        <p className="upload-text">
                                            Przeciągnij obrazy tutaj lub kliknij, aby je wybrać
                                        </p>
                                        <span className="upload-hint">
                                            Możesz wybrać kilka plików jednocześnie
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="gallery-grid">
                                {galleryImages.map((image, index) => (
                                    <div key={image.id || index} className="gallery-item">
                                        <div className="gallery-image-wrapper">
                                            <img 
                                                src={image.path} 
                                                alt={image.caption || `Obraz ${index + 1}`}
                                                className="gallery-image"
                                                loading="lazy"
                                            />
                                            <div className="gallery-item-overlay">
                                                <button 
                                                    className="gallery-delete-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteImage(image.id);
                                                    }}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </div>
                                        <input 
                                            type="text"
                                            className="gallery-caption-input"
                                            placeholder="Podpis obrazu"
                                            value={image.caption || ''}
                                            onChange={(e) => {
                                                const newImages = [...galleryImages];
                                                newImages[index].caption = e.target.value;
                                                setGalleryImages(newImages);
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>

                            {galleryImages.length === 0 && !uploading && (
                                <div className="gallery-empty">
                                    <span className="empty-icon">🖼️</span>
                                    <p>Brak wczytanych obrazów</p>
                                </div>
                            )}
                        </div>

                        <div className="edit-event-actions">
                            <button className="btn-apply" onClick={handleSubmit}>
                                {buttonText}
                            </button>

                            <button className="btn-cancel" onClick={handleCancel}>
                                Anuluj
                            </button>
                        </div>
                    </div>
                </div>
            </MiddleBlock>
        </>
    )
}
